import { ReactNode } from 'react';

import { useQuery } from '@tanstack/react-query';
import parse, { DOMNode, Element } from 'html-react-parser';
import { useTranslation } from 'react-i18next';

import { SearchDocument } from '@cfa/pathway-fe-shared/xp';

import {
  documentHtmlQueryOptions,
  extractBodyContent,
  extractTridionMediaDescriptionHtml,
  fixTridionImageSources,
} from '@/utils/document-utils';
import { cn } from '@/utils/tailwind';

import {
  BrightcovePlayer,
  htmlHasMultipleBrightcoveAccounts,
  isBrightcovePlayerUrl,
} from '@/components/BrightcovePlayer';
import { SafeImg } from '@/components/SafeImage';

import Constants from '@/constants';

import PdfContent from '../../tridion-components/PdfContent';
import TridionGameContent from '../../tridion-components/TridionGameContent';

import { Games, Video } from '@/icons';

interface UseTridionDocumentOptions {
  documentType?: SearchDocument['type'];
  documentName?: string | null;
  fallbackVideoUrl?: string;
  icon?: string;
}

interface TridionTypeConfig {
  iconContent: ReactNode;
  documentContent: ReactNode;
}

/** Swap Brightcove iframes in Tridion HTML for in-page players (heartbeat idle). */
const replaceBrightcoveIframes = (domNode: DOMNode) => {
  if (!(domNode instanceof Element) || domNode.name !== 'iframe') {
    return undefined;
  }

  const src = domNode.attribs.src;
  if (!isBrightcovePlayerUrl(src)) {
    return undefined;
  }

  return (
    <BrightcovePlayer
      className="aspect-video w-full"
      src={src}
      title={domNode.attribs.title ?? 'Video'}
    />
  );
};

/**
 * In-page Brightcove embeds share a global `bc` registry keyed by player id.
 * Documents that mix accounts (both typically `default_default`) must keep
 * iframes so each account stays isolated in its own browsing context.
 */
const parseTridionHtml = (html: string) => {
  if (htmlHasMultipleBrightcoveAccounts(html)) {
    return parse(html);
  }
  return parse(html, { replace: replaceBrightcoveIframes });
};

export const useTridion = (
  url?: string,
  options?: UseTridionDocumentOptions,
): { htmlContent: string | undefined; error: boolean } & TridionTypeConfig => {
  const { t } = useTranslation();

  const { data: html, isError } = useQuery({
    ...documentHtmlQueryOptions(url ?? ''),
    enabled: !!url,
  });

  const resolvedHtmlContent = html ? extractBodyContent(html) : undefined;
  const resolvedError = !url || isError;
  const contentFixedSrc = fixTridionImageSources(resolvedHtmlContent);

  // Extract iframe src from HTML content if present, otherwise fall back to fallbackVideoUrl
  const resolvedVideoUrl = (() => {
    if (contentFixedSrc) {
      const iframeMatch = /<iframe[^>]+src=["']([^"']+)["']/i.exec(
        contentFixedSrc,
      );
      if (iframeMatch?.[1]) {
        return iframeMatch[1];
      }
    }
    return options?.fallbackVideoUrl;
  })();

  const iconName =
    options?.icon === undefined || options.icon === 'placeholder'
      ? 'Chickfila'
      : options.icon;
  const defaultIcon = (
    <SafeImg
      className={cn(
        'size-[56px] object-contain',
        iconName === 'Chickfila' && 'dark:brightness-0 dark:invert',
      )}
      alt="document icon"
      src={`${Constants.PATHWAY_CDN_IMG.PROCEDURES}${iconName}.svg`}
      fallback={`${Constants.PATHWAY_CDN_IMG.PROCEDURES}Chickfila.svg`}
    />
  );

  const typeConfig = ((): Partial<TridionTypeConfig> | null => {
    switch (options?.documentType?.toUpperCase()) {
      case 'PDF':
        return {
          documentContent: (
            <PdfContent htmlContent={contentFixedSrc ?? resolvedHtmlContent} />
          ),
        };

      case 'VIDEO': {
        const descriptionHtml = extractTridionMediaDescriptionHtml(
          contentFixedSrc ?? resolvedHtmlContent,
        );
        const videoTitle = options.documentName ?? 'Video content';

        return {
          iconContent: (
            <Video
              className="size-14"
              role="img"
              aria-label={t('video.icon.label')}
            />
          ),
          documentContent: (
            <div className="flex flex-col gap-5">
              {resolvedVideoUrl && isBrightcovePlayerUrl(resolvedVideoUrl) ? (
                <BrightcovePlayer
                  className="aspect-video w-full"
                  src={resolvedVideoUrl}
                  title={videoTitle}
                />
              ) : (
                <iframe
                  className="aspect-video w-full"
                  allowFullScreen
                  src={resolvedVideoUrl}
                  title={videoTitle}
                />
              )}
              {descriptionHtml ? (
                <div data-testid="VideoDescription">
                  {parse(descriptionHtml)}
                </div>
              ) : null}
            </div>
          ),
        };
      }

      case 'GAME':
        return {
          iconContent: (
            <Games
              className="size-14"
              role="img"
              aria-label={t('games.iconLabel')}
            />
          ),
          documentContent: (
            <TridionGameContent
              htmlContent={contentFixedSrc ?? resolvedHtmlContent}
            />
          ),
        };
    }
    return null;
  })();

  const parsedContent = contentFixedSrc
    ? parseTridionHtml(contentFixedSrc)
    : null;

  return {
    htmlContent: contentFixedSrc,
    error: resolvedError,
    iconContent: typeConfig?.iconContent ?? defaultIcon,
    documentContent: typeConfig?.documentContent ?? parsedContent,
  };
};
